import { CognitoIdentityClient, GetIdCommand, GetCredentialsForIdentityCommand } from '@aws-sdk/client-cognito-identity';
import { awsConfig } from './awsConfig';
import pkceChallenge from 'pkce-challenge';

const cognitoIdentityClient = new CognitoIdentityClient({ region: awsConfig.region });

export const redirectToHostedUI = async () => {
    const { code_verifier, code_challenge } = await pkceChallenge();
    localStorage.setItem('codeVerifier', code_verifier);

    const loginUrl = `https://${awsConfig.hostedUiPrefix}.auth.${awsConfig.region}.amazoncognito.com/oauth2/authorize?response_type=code&client_id=${awsConfig.userPoolWebClientId}&redirect_uri=${window.location.origin}/callback&code_challenge=${code_challenge}&code_challenge_method=S256`;
    window.location.href = loginUrl;
};

export const handleCallback = async () => {
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get('code');
    const codeVerifier = localStorage.getItem('codeVerifier');

    if (!code || !codeVerifier) {
        throw new Error('Authorization code or code verifier not found');
    }

    localStorage.removeItem('codeVerifier');

    const tokenUrl = `https://${awsConfig.hostedUiPrefix}.auth.${awsConfig.region}.amazoncognito.com/oauth2/token`;
    const body = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: awsConfig.userPoolWebClientId,
        code,
        redirect_uri: `${window.location.origin}/callback`,
        code_verifier: codeVerifier,
    });

    const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body,
    });

    const tokenData = await response.json();
    const idToken = tokenData.id_token;

    if (!idToken) {
        throw new Error('Authentication failed');
    }

    // Store id_token with expiration
    const expiresIn = tokenData.expires_in || 3600; // Default 1 hour if not provided
    const expirationTime = new Date(Date.now() + expiresIn * 1000).toISOString();
    localStorage.setItem('idToken', JSON.stringify({
        token: idToken,
        expiration: expirationTime
    }));

    await assumeRoleWithToken(idToken);
};

export const assumeRoleWithToken = async (idToken: string) => {
    const getIdCommand = new GetIdCommand({
        IdentityPoolId: awsConfig.identityPoolId,
        Logins: {
            [`cognito-idp.${awsConfig.region}.amazonaws.com/${awsConfig.userPoolId}`]: idToken,
        },
    });

    const getIdResponse = await cognitoIdentityClient.send(getIdCommand);
    const identityId = getIdResponse.IdentityId;

    if (!identityId) {
        throw new Error('Failed to get identity ID');
    }

    const getCredentialsCommand = new GetCredentialsForIdentityCommand({
        IdentityId: identityId,
        Logins: {
            [`cognito-idp.${awsConfig.region}.amazonaws.com/${awsConfig.userPoolId}`]: idToken,
        },
    });

    const credentialsResponse = await cognitoIdentityClient.send(getCredentialsCommand);
    const credentials = credentialsResponse.Credentials;

    if (credentials) {
        localStorage.setItem('awsCredentials', JSON.stringify(credentials));
        return credentials;
    }

    throw new Error('Failed to get AWS credentials');
};

export const getStoredCredentials = async () => {
    const storedCredentials = localStorage.getItem('awsCredentials');
    if (storedCredentials) {
        const credentials = JSON.parse(storedCredentials);
        const expiration = new Date(credentials.Expiration);

        if (expiration > new Date()) {
            return credentials;
        }
    }

    // Try to refresh credentials using stored idToken
    const storedIdToken = localStorage.getItem('idToken');
    if (storedIdToken) {
        const { token, expiration } = JSON.parse(storedIdToken);
        if (new Date(expiration) > new Date()) {
            try {
                return await assumeRoleWithToken(token);
            } catch (error) {
                // If refresh fails, redirect to login
                await redirectToHostedUI();
                return null;
            }
        }
    }

    // If no valid idToken, redirect to login
    await redirectToHostedUI();

    return null;
};